/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { ReactNode } from "react";

import { useTranslation } from "react-i18next";

import { useTableStoreByRoute } from "@/stores/useTableStore";
import Typography from "@mui/material/Typography";

import { CalendarStandartIcon, colors } from "@dotnet/telematics-design-system";

import {
  DRIVERS_ACTIONS,
  ROUTES,
  TABLE_NAMES,
  tableNameParams,
  TUTORIAL_IDS,
  useFormattedNumber,
  useMediaQueries,
  usePageHeaderStyles,
  VEHICLES_ACTIONS
} from "../../shared";
import HeaderTitleLoader from "../../shared/components/loaders/HeaderTitleLoader";
import Button from "../Button/Button";
import InfoIconWithTooltip from "../IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";
import SchedulesMainDialog from "../PageHeader/actions/dialogs/reusableDialogs/SchedulesMainDialog/SchedulesMainDialog";
import { useDialog } from "../PageHeader/actions/hooks";
import useTutorialMode from "../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../TutorialMode/types";

interface IPageHeaderProps {
  route: ROUTES;
  children: ReactNode;
  tableName: TABLE_NAMES;
  infoIconTooltip?: string;
}

const PageHeader = ({
  route,
  children,
  tableName,
  infoIconTooltip
}: IPageHeaderProps) => {
  const {
    selectedRows,
    initialLoading,
    pagination,
    params: { desktopDetailsSection }
  } = useTableStoreByRoute(route);

  const { toMd, toSm, fromMd, toLg } = useMediaQueries();
  const { t } = useTranslation();
  const { renderFormattedNumber } = useFormattedNumber();

  const { isDialogOpen, openDialog, closeDialog } = useDialog();
  const {
    stepIndex,
    stepGroup,
    checkIfCorrectTutorialStep,
    setTutorialDetails
  } = useTutorialMode();

  const isDesktopDetailsOpen = desktopDetailsSection;

  const dashboardPage = tableName === TABLE_NAMES.Dashboard;

  const { pageHeader, pageHeaderTitle, pageHeaderActions } =
    usePageHeaderStyles(true, false);

  const resourceParams = tableNameParams[tableName] || {};

  const countOfTableItems = pagination?.totalCount || 0;

  const showIconOnly = toMd || (fromMd && toLg && isDesktopDetailsOpen);

  const handleVehicleSchedulesOpen = () => {
    const createScheduleStepIndex = toMd ? 3 : 2;
    const applyScheduleStepIndex = toMd ? 4 : 2;

    const createScheduleTutorialStep = checkIfCorrectTutorialStep(
      STEP_GROUP.TutorialCreateSchedule,
      createScheduleStepIndex
    );

    const applycheduleTutorialStep = checkIfCorrectTutorialStep(
      STEP_GROUP.TutorialApplySchedule,
      applyScheduleStepIndex
    );

    if (createScheduleTutorialStep || applycheduleTutorialStep) {
      setTutorialDetails({
        isTutorialOpen: false,
        stepIndex: stepIndex + 1,
        stepGroup,
        isTutorialActive: true
      });
    }

    openDialog(VEHICLES_ACTIONS.Schedules);
  };

  const handleSchedulesButtonClick = () => {
    switch (tableName) {
      case TABLE_NAMES.Vehicles:
        return handleVehicleSchedulesOpen();

      case TABLE_NAMES.Drivers:
        return openDialog(DRIVERS_ACTIONS.Schedules);

      default:
        return;
    }
  };

  const schedulesButtonExists =
    tableName === TABLE_NAMES.Vehicles || tableName === TABLE_NAMES.Drivers;

  const schedulesMainAction = resourceParams?.actionMain;

  return (
    <div css={css([pageHeader])}>
      <div css={css(pageHeaderTitle)}>
        {initialLoading ? (
          <HeaderTitleLoader dashboardPage={dashboardPage} />
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{ color: colors.textDark, marginRight: "5px" }}
            >
              {t(`General##${tableName}`)}
            </Typography>
            {!dashboardPage && (
              <Typography variant="h6" sx={{ color: colors.textPlaceholder }}>
                ({pagination ? renderFormattedNumber(countOfTableItems) : 0})
              </Typography>
            )}
            {infoIconTooltip && (
              <InfoIconWithTooltip
                title={t(infoIconTooltip)}
                customIconStyle={{ marginLeft: "4px" }}
                placement="right"
              />
            )}

            {schedulesButtonExists && (
              <Button
                id={TUTORIAL_IDS.SchedulesDialogButton}
                fullWidth={toSm}
                hasRectangleLoader={toSm}
                variant={showIconOnly ? "iconOnly" : "iconLeft"}
                size="small"
                color={selectedRows.length > 0 ? "primary" : "white"}
                icon={
                  <CalendarStandartIcon
                    style={{ width: "24px", height: "24px" }}
                  />
                }
                onClick={handleSchedulesButtonClick}
                isInitialLoading={initialLoading}
                loaderWidth="100%"
                highlightAction={selectedRows.length > 0}
                css={css([
                  {
                    marginLeft: toSm ? "auto" : "16px"
                  },
                  showIconOnly && { width: "36px" }
                ])}
              >
                {t("Button##schedules")}
              </Button>
            )}
          </>
        )}
      </div>
      <div css={css(pageHeaderActions)}>{children}</div>

      <SchedulesMainDialog
        route={route}
        isOpen={schedulesMainAction ? isDialogOpen[schedulesMainAction] : false}
        resourceParams={resourceParams}
        onClose={closeDialog}
      />
    </div>
  );
};

export default PageHeader;
